/** @format */

import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { toast } from "react-hot-toast";
import {
  XMarkIcon,
  CheckIcon,
  GlobeAltIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";

const GeneralSettingsModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "Profs Training Solutions",
    address: {
      street: "Fefeti Drive, Spintex Road",
      city: "Tema",
      region: "Greater Accra",
      country: "Ghana",
    },
    contact: {
      phones: ["+233 243 44 39 34", "+233 244 990 773", "+233 246 752 130"],
      workingHours: "Mon-Fri: 9am-5pm",
    },
    emails: ["profsa35@gmail.com", "profstrainingsolutions@gmail.com"],
    socials: {
      instagram: "http://instagram.com/profs_training_solutions/",
      tiktok: "http://tiktok.com/@profstraining",
      youtube: "http://youtube.com/@ProfsTrainingSolutions",
      linkedin: "https://linkedin.com/in/profs-training-solutions/",
      facebook: "http://facebook.com/ProfsTraining",
      whatsapp: "https://whatsapp.com/channel/0029VaZV6OeEAKWHHdSdiR1S",
      telegram: "https://t.me/Profstrainingsolutions",
    },
    studyApps: {
      playStore:
        "https://play.google.com/store/apps/details?id=profstudymate.appev",
      appleStore: "https://apps.apple.com/app/profstudymate",
      microsoftStore: "https://www.microsoft.com/store/apps/profstudymate",
      webApp: "https://profstudymate.com/#",
    },
  });

  useEffect(() => {
    if (isOpen) {
      loadGeneralSettings();
    }
  }, [isOpen]);

  const loadGeneralSettings = async () => {
    setLoading(true);
    try {
      const settingsDoc = await getDoc(doc(db, "generalSettings", "main"));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setFormData((prev) => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error("Error loading general settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleArrayChange = (section, field, index, value) => {
    setFormData((prev) => {
      const newArray = [...prev[section][field]];
      newArray[index] = value;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newArray,
        },
      };
    });
  };

  const addArrayItem = (section, field) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...prev[section][field], ""],
      },
    }));
  };

  const removeArrayItem = (section, field, index) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index),
      },
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "generalSettings", "main"), {
        ...formData,
        updatedAt: new Date(),
      });
      toast.success("General settings saved successfully!");
      onClose();
    } catch (error) {
      console.error("Error saving general settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">General Settings</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading settings...</p>
          </div>
        ) : (
          <div className="space-y-6 max-h-96 overflow-y-auto">
            {/* Company Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <GlobeAltIcon className="w-5 h-5 mr-2" />
                Company Information
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) =>
                      handleInputChange(null, "companyName", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="w-5 h-5 mr-2" />
                Address
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street
                  </label>
                  <input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) =>
                      handleInputChange("address", "street", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) =>
                      handleInputChange("address", "city", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Region
                  </label>
                  <input
                    type="text"
                    value={formData.address.region}
                    onChange={(e) =>
                      handleInputChange("address", "region", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.address.country}
                    onChange={(e) =>
                      handleInputChange("address", "country", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <PhoneIcon className="w-5 h-5 mr-2" />
                Contact Information
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Numbers
                  </label>
                  {formData.contact.phones.map((phone, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-2 mb-2"
                    >
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) =>
                          handleArrayChange(
                            "contact",
                            "phones",
                            index,
                            e.target.value
                          )
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Phone number"
                      />
                      <button
                        onClick={() =>
                          removeArrayItem("contact", "phones", index)
                        }
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                        disabled={formData.contact.phones.length === 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem("contact", "phones")}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add Phone Number
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Working Hours
                  </label>
                  <input
                    type="text"
                    value={formData.contact.workingHours}
                    onChange={(e) =>
                      handleInputChange(
                        "contact",
                        "workingHours",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Mon-Fri: 9am-5pm"
                  />
                </div>
              </div>
            </div>

            {/* Email Addresses */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <EnvelopeIcon className="w-5 h-5 mr-2" />
                Email Addresses
              </h4>
              <div className="space-y-4">
                {formData.emails.map((email, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) =>
                        handleArrayChange("emails", null, index, e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Email address"
                    />
                    <button
                      onClick={() => removeArrayItem("emails", null, index)}
                      className="px-3 py-2 text-red-600 hover:text-red-800"
                      disabled={formData.emails.length === 1}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem("emails", null)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  + Add Email Address
                </button>
              </div>
            </div>

            {/* Social Media Links */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <ShareIcon className="w-5 h-5 mr-2" />
                Social Media Links
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram
                  </label>
                  <input
                    type="url"
                    value={formData.socials.instagram}
                    onChange={(e) =>
                      handleInputChange("socials", "instagram", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    TikTok
                  </label>
                  <input
                    type="url"
                    value={formData.socials.tiktok}
                    onChange={(e) =>
                      handleInputChange("socials", "tiktok", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    YouTube
                  </label>
                  <input
                    type="url"
                    value={formData.socials.youtube}
                    onChange={(e) =>
                      handleInputChange("socials", "youtube", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    value={formData.socials.linkedin}
                    onChange={(e) =>
                      handleInputChange("socials", "linkedin", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facebook
                  </label>
                  <input
                    type="url"
                    value={formData.socials.facebook}
                    onChange={(e) =>
                      handleInputChange("socials", "facebook", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Channel
                  </label>
                  <input
                    type="url"
                    value={formData.socials.whatsapp}
                    onChange={(e) =>
                      handleInputChange("socials", "whatsapp", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telegram
                  </label>
                  <input
                    type="url"
                    value={formData.socials.telegram}
                    onChange={(e) =>
                      handleInputChange("socials", "telegram", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Study Apps */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <DevicePhoneMobileIcon className="w-5 h-5 mr-2" />
                Study Apps
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google Play Store
                  </label>
                  <input
                    type="url"
                    value={formData.studyApps.playStore}
                    onChange={(e) =>
                      handleInputChange(
                        "studyApps",
                        "playStore",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apple App Store
                  </label>
                  <input
                    type="url"
                    value={formData.studyApps.appleStore}
                    onChange={(e) =>
                      handleInputChange(
                        "studyApps",
                        "appleStore",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Microsoft Store
                  </label>
                  <input
                    type="url"
                    value={formData.studyApps.microsoftStore}
                    onChange={(e) =>
                      handleInputChange(
                        "studyApps",
                        "microsoftStore",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Web Application
                  </label>
                  <input
                    type="url"
                    value={formData.studyApps.webApp}
                    onChange={(e) =>
                      handleInputChange("studyApps", "webApp", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <CheckIcon className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettingsModal;
